import { Stack } from '@mantine/core';
import { Banner, Button, Text, Title } from '@repo/foundations';
import { upperFirst } from 'lodash-es';
import {
	useIntegrationOAuthConnect,
	useIntegrationOAuthDisconnect,
	useIntegrationOAuthStatus,
} from './hooks/useIntegrationOAuth';

interface IntegrationOauthConnectControllerProps {
	integrationId: string;
	integrationType: string;
	isOauthEnabled: boolean;
	openInNewTab?: boolean;
	onOauthInitiated?: () => void;
}

export function IntegrationOauthConnectController({
	integrationId,
	integrationType,
	isOauthEnabled,
	openInNewTab = false,
	onOauthInitiated,
}: IntegrationOauthConnectControllerProps): JSX.Element | null {
	const integrationName = upperFirst(integrationType);

	const { data: status, isLoading } = useIntegrationOAuthStatus(
		integrationId,
		integrationType,
		isOauthEnabled
	);

	const connectMutation = useIntegrationOAuthConnect(
		integrationId,
		integrationType,
		{ openInNewTab, onSuccess: onOauthInitiated }
	);

	const disconnectMutation = useIntegrationOAuthDisconnect(
		integrationId,
		integrationType
	);

	if (!isOauthEnabled) {
		return null;
	}

	const isConnected = status?.connected ?? false;

	return (
		<Stack spacing="md">
			<Title size="md">User authentication</Title>
			{isConnected ? (
				<Banner
					tone="success"
					title={`Connected to ${integrationName}`}
					message="Your account is linked. Queries will run with your user permissions."
				/>
			) : null}
			<Text size="sm" color="text/secondary/default">
				Authenticate once to enable {integrationName} to enforce your user
				permissions on queries executed in Secoda.
			</Text>
			{isConnected ? (
				<Button
					variant="tertiary"
					tone="critical"
					onClick={() => disconnectMutation.mutate()}
					loading={disconnectMutation.isPending}
				>
					Disconnect
				</Button>
			) : (
				<Button
					variant="primary"
					onClick={() => connectMutation.mutate()}
					loading={connectMutation.isPending || isLoading}
				>
					Authenticate
				</Button>
			)}
		</Stack>
	);
}
